import { api, PortfolioItem, PortfolioPayload, PortfolioResponse } from '../api/client';

interface PortfolioState {
  items: PortfolioItem[];
  summary: PortfolioResponse['summary'] | null;
  editingId: number | null;
}

const state: PortfolioState = {
  items: [],
  summary: null,
  editingId: null,
};

export async function renderPortfolio(container: HTMLElement) {
  container.innerHTML = `
    <div class="card">
      <h2 class="card-header">Portfolio</h2>
      <div id="portfolio-message" class="alert" style="display:none;"></div>

      <div id="portfolio-summary" class="portfolio-summary-grid"></div>

      <div class="portfolio-grid">
        <form id="portfolio-form" class="portfolio-form">
          <h3 id="portfolio-form-title">Add Domain</h3>
          <div class="form-group">
            <label class="form-label" for="portfolio-domain">Domain Name</label>
            <input id="portfolio-domain" class="form-input" placeholder="example.com" required />
          </div>
          <div class="form-group">
            <label class="form-label" for="portfolio-purchase-price">Purchase Price (USD)</label>
            <input id="portfolio-purchase-price" type="number" class="form-input" min="0" step="0.01" required />
          </div>
          <div class="form-group">
            <label class="form-label" for="portfolio-purchase-date">Purchase Date</label>
            <input id="portfolio-purchase-date" type="date" class="form-input" required />
          </div>
          <div class="form-group">
            <label class="form-label" for="portfolio-renewal-price">Yearly Renewal Price (USD)</label>
            <input id="portfolio-renewal-price" type="number" class="form-input" min="0" step="0.01" required />
          </div>
          <div class="form-group portfolio-checkbox-row">
            <label class="form-label" for="portfolio-is-sold">Sold</label>
            <input id="portfolio-is-sold" type="checkbox" />
          </div>
          <div id="portfolio-sale-fields" class="portfolio-sale-fields" style="display:none;">
            <div class="form-group">
              <label class="form-label" for="portfolio-sale-price">Sale Price (USD)</label>
              <input id="portfolio-sale-price" type="number" class="form-input" min="0" step="0.01" />
            </div>
            <div class="form-group">
              <label class="form-label" for="portfolio-sale-date">Sale Date (optional)</label>
              <input id="portfolio-sale-date" type="date" class="form-input" />
            </div>
          </div>
          <div class="portfolio-form-actions">
            <button class="btn btn-primary" id="portfolio-submit-btn" type="submit">Save Domain</button>
            <button class="btn btn-secondary" id="portfolio-cancel-edit" type="button" style="display:none;">Cancel Edit</button>
          </div>
        </form>

        <div class="portfolio-list-wrap">
          <h3>Domains</h3>
          <div id="portfolio-list" class="portfolio-list"></div>
        </div>
      </div>
    </div>
  `;

  bindPortfolioEvents(container);
  await loadPortfolioData(container);
}

function bindPortfolioEvents(container: HTMLElement) {
  const soldCheckbox = container.querySelector('#portfolio-is-sold') as HTMLInputElement;
  const saleFields = container.querySelector('#portfolio-sale-fields') as HTMLElement;
  const form = container.querySelector('#portfolio-form') as HTMLFormElement;
  const cancelEditBtn = container.querySelector('#portfolio-cancel-edit') as HTMLButtonElement;

  soldCheckbox.addEventListener('change', () => {
    toggleSaleFields(soldCheckbox.checked, saleFields);
  });

  cancelEditBtn.addEventListener('click', () => {
    resetForm(container);
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    await submitPortfolioForm(container);
  });
}

async function loadPortfolioData(container: HTMLElement) {
  const message = container.querySelector('#portfolio-message') as HTMLElement;
  const list = container.querySelector('#portfolio-list') as HTMLElement;

  list.innerHTML = `
    <div class="loading">
      <div class="spinner"></div>
      <p>Loading portfolio...</p>
    </div>
  `;

  const response = await api.listPortfolio();
  if (!response.success || !response.data) {
    showMessage(message, response.error || 'Failed to load portfolio', 'error');
    list.innerHTML = '';
    return;
  }

  state.items = response.data.items;
  state.summary = response.data.summary;
  renderSummary(container);
  renderPortfolioList(container);
}

function renderSummary(container: HTMLElement) {
  const summaryWrap = container.querySelector('#portfolio-summary') as HTMLElement;
  const summary = state.summary;
  if (!summary) {
    summaryWrap.innerHTML = '';
    return;
  }

  summaryWrap.innerHTML = `
    <article class="portfolio-stat">
      <span class="portfolio-stat__label">Portfolio Value</span>
      <span class="portfolio-stat__value">${formatMoney(summary.total_portfolio_value)}</span>
    </article>
    <article class="portfolio-stat">
      <span class="portfolio-stat__label">Cost Basis</span>
      <span class="portfolio-stat__value">${formatMoney(summary.total_cost_basis)}</span>
    </article>
    <article class="portfolio-stat">
      <span class="portfolio-stat__label">Profit</span>
      <span class="portfolio-stat__value ${summary.total_profit >= 0 ? 'is-profit' : 'is-loss'}">${formatMoney(summary.total_profit)}</span>
    </article>
    <article class="portfolio-stat">
      <span class="portfolio-stat__label">ROI</span>
      <span class="portfolio-stat__value ${summary.roi_percent >= 0 ? 'is-profit' : 'is-loss'}">${summary.roi_percent.toFixed(2)}%</span>
    </article>
  `;
}

function renderPortfolioList(container: HTMLElement) {
  const list = container.querySelector('#portfolio-list') as HTMLElement;
  if (state.items.length === 0) {
    list.innerHTML = '<div class="empty-state"><p>No domains in your portfolio yet.</p></div>';
    return;
  }

  list.innerHTML = state.items
    .map((item) => {
      const sold = item.is_sold === 1;
      return `
        <article class="portfolio-item" data-id="${item.id}">
          <div class="portfolio-item__top">
            <div class="portfolio-item__domain">${escapeHtml(item.domain_name)}</div>
            <span class="listing-type ${sold ? 'buy-now' : 'auction'}">${sold ? 'sold' : 'holding'}</span>
          </div>
          <div class="portfolio-item__meta">
            <div><span>Purchased</span><strong>${formatMoney(item.purchase_price)} on ${item.purchase_date}</strong></div>
            <div><span>Renewal</span><strong>${formatMoney(item.yearly_renewal_price)} / yr</strong></div>
            <div><span>Cost Basis</span><strong>${formatMoney(item.total_cost_basis)}</strong></div>
            <div><span>Current Value</span><strong>${formatMoney(item.current_value)}</strong></div>
            <div><span>Profit</span><strong class="${item.profit >= 0 ? 'is-profit' : 'is-loss'}">${formatMoney(item.profit)}</strong></div>
            <div><span>Renewal Years</span><strong>${item.renewal_years}</strong></div>
          </div>
          <div class="portfolio-item__actions">
            <button class="btn btn-secondary portfolio-edit-btn" data-edit-id="${item.id}">Edit</button>
          </div>
        </article>
      `;
    })
    .join('');

  list.querySelectorAll('.portfolio-edit-btn').forEach((button) => {
    button.addEventListener('click', () => {
      const idText = (button as HTMLButtonElement).dataset.editId;
      const id = Number(idText);
      if (!Number.isInteger(id)) return;
      const item = state.items.find((entry) => entry.id === id);
      if (!item) return;
      populateFormForEdit(container, item);
    });
  });
}

function populateFormForEdit(container: HTMLElement, item: PortfolioItem) {
  state.editingId = item.id;
  (container.querySelector('#portfolio-form-title') as HTMLElement).textContent = 'Edit Domain';
  (container.querySelector('#portfolio-submit-btn') as HTMLElement).textContent = 'Update Domain';
  (container.querySelector('#portfolio-cancel-edit') as HTMLElement).style.display = 'inline-flex';

  (container.querySelector('#portfolio-domain') as HTMLInputElement).value = item.domain_name;
  (container.querySelector('#portfolio-purchase-price') as HTMLInputElement).value = String(item.purchase_price);
  (container.querySelector('#portfolio-purchase-date') as HTMLInputElement).value = item.purchase_date;
  (container.querySelector('#portfolio-renewal-price') as HTMLInputElement).value = String(item.yearly_renewal_price);
  const soldCheckbox = container.querySelector('#portfolio-is-sold') as HTMLInputElement;
  soldCheckbox.checked = item.is_sold === 1;
  (container.querySelector('#portfolio-sale-price') as HTMLInputElement).value = item.sale_price != null ? String(item.sale_price) : '';
  (container.querySelector('#portfolio-sale-date') as HTMLInputElement).value = item.sale_date ?? '';

  toggleSaleFields(soldCheckbox.checked, container.querySelector('#portfolio-sale-fields') as HTMLElement);
}

function resetForm(container: HTMLElement) {
  const form = container.querySelector('#portfolio-form') as HTMLFormElement;
  form.reset();
  state.editingId = null;
  (container.querySelector('#portfolio-form-title') as HTMLElement).textContent = 'Add Domain';
  (container.querySelector('#portfolio-submit-btn') as HTMLElement).textContent = 'Save Domain';
  (container.querySelector('#portfolio-cancel-edit') as HTMLElement).style.display = 'none';
  toggleSaleFields(false, container.querySelector('#portfolio-sale-fields') as HTMLElement);
}

async function submitPortfolioForm(container: HTMLElement) {
  const message = container.querySelector('#portfolio-message') as HTMLElement;
  const domainName = (container.querySelector('#portfolio-domain') as HTMLInputElement).value.trim().toLowerCase();
  const purchasePrice = Number((container.querySelector('#portfolio-purchase-price') as HTMLInputElement).value);
  const purchaseDate = (container.querySelector('#portfolio-purchase-date') as HTMLInputElement).value;
  const yearlyRenewalPrice = Number((container.querySelector('#portfolio-renewal-price') as HTMLInputElement).value);
  const isSold = (container.querySelector('#portfolio-is-sold') as HTMLInputElement).checked;
  const salePriceInput = (container.querySelector('#portfolio-sale-price') as HTMLInputElement).value;
  const saleDateInput = (container.querySelector('#portfolio-sale-date') as HTMLInputElement).value;

  if (!domainName || !purchaseDate || !Number.isFinite(purchasePrice) || !Number.isFinite(yearlyRenewalPrice)) {
    showMessage(message, 'Please complete all required fields', 'error');
    return;
  }

  const payload: PortfolioPayload = {
    domain_name: domainName,
    purchase_price: purchasePrice,
    purchase_date: purchaseDate,
    yearly_renewal_price: yearlyRenewalPrice,
    is_sold: isSold,
    sale_price: isSold && salePriceInput ? Number(salePriceInput) : null,
    sale_date: isSold && saleDateInput ? saleDateInput : null,
  };

  const response = state.editingId == null
    ? await api.createPortfolioItem(payload)
    : await api.updatePortfolioItem(state.editingId, payload);

  if (!response.success) {
    showMessage(message, response.error || 'Failed to save domain', 'error');
    return;
  }

  showMessage(message, state.editingId == null ? 'Domain added to portfolio' : 'Domain updated', 'success');
  resetForm(container);
  await loadPortfolioData(container);
}

function toggleSaleFields(show: boolean, saleFields: HTMLElement) {
  saleFields.style.display = show ? 'block' : 'none';
  const salePrice = saleFields.querySelector('#portfolio-sale-price') as HTMLInputElement | null;
  if (salePrice) {
    salePrice.required = show;
  }
}

function showMessage(container: HTMLElement, text: string, kind: 'success' | 'error') {
  container.textContent = text;
  container.className = kind === 'success' ? 'alert alert-success' : 'alert alert-error';
  container.style.display = 'block';
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
